import Image from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const DEFAULT_IS_ALLOWED_SRC = (src: string) => /^https?:/i.test(src);

/**
 * Wraps TipTap's Image extension to restrict `src` to an allowed set of URI
 * schemes/hosts (http/https only by default). `@tiptap/extension-image` only
 * blocks `data:` at HTML-parse time and otherwise allows any scheme or host,
 * including third-party tracking-pixel hosts.
 *
 * Disallowed `src` values are stripped from the document model (set to `null`)
 * on every entry path — HTML (`parseHTML`), markdown (`parseMarkdown`), and
 * programmatic insert/JSON (`appendTransaction`) — so they never reach
 * `onJsonChange` / `onMarkdownChange`. `renderHTML` still paints a blocked
 * placeholder for nodes whose `src` was cleared.
 */
export const createSafeImageExtension = (
  isAllowedSrc: (src: string) => boolean = DEFAULT_IS_ALLOWED_SRC,
) =>
  Image.extend({
    parseHTML() {
      // Always match img[src] and let isAllowedSrc be the single source of
      // truth for which schemes are allowed — the stock extension's
      // `:not([src^="data:"])` selector would otherwise reject data: URLs
      // before a custom isAllowedSrc that explicitly allows them ever runs.
      return [
        {
          tag: "img[src]",
          getAttrs: (element) =>
            isAllowedSrc((element as HTMLElement).getAttribute("src") ?? "")
              ? {}
              : false,
        },
      ];
    },
    parseMarkdown: (token, helpers) => {
      const src = token.href ?? "";
      return helpers.createNode("image", {
        // Keep the node so renderHTML can show a blocked placeholder, but
        // never persist a disallowed href into the document model.
        src: isAllowedSrc(src) ? src : null,
        title: token.title,
        alt: token.text,
      });
    },
    renderMarkdown: (node) => {
      const src = node.attrs?.src ?? "";
      if (!isAllowedSrc(src)) return "";
      const alt = node.attrs?.alt ?? "";
      const title = node.attrs?.title ?? "";
      return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`;
    },
    renderHTML({ HTMLAttributes }) {
      if (!isAllowedSrc(HTMLAttributes.src ?? "")) {
        return ["span", { "data-anv-blocked-image": "true" }];
      }
      return [
        "img",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      ];
    },
    addProseMirrorPlugins() {
      return [
        ...(this.parent?.() ?? []),
        new Plugin({
          key: new PluginKey("anvSafeImageSrc"),
          // Covers setImage, input rules, and JSON setContent — paths that
          // never hit parseHTML/parseMarkdown.
          appendTransaction: (_transactions, _oldState, newState) => {
            const dirty: { pos: number; attrs: Record<string, unknown> }[] = [];
            newState.doc.descendants((node, pos) => {
              if (
                node.type.name === "image" &&
                node.attrs.src != null &&
                !isAllowedSrc(node.attrs.src)
              ) {
                dirty.push({
                  pos,
                  attrs: { ...node.attrs, src: null },
                });
              }
            });
            if (dirty.length === 0) return null;
            const tr = newState.tr;
            for (let i = dirty.length - 1; i >= 0; i--) {
              const { pos, attrs } = dirty[i]!;
              tr.setNodeMarkup(pos, undefined, attrs);
            }
            return tr;
          },
        }),
      ];
    },
  });
